import TrophyBodySvg from './TrophyBodySvg';
import { LEVEL_BODY_COLORS } from './constants';
import { TrophyContainer, LayerImg } from './styled-components';

// ── Extra layer assets (unique shapes, kept as <img>) ───────────────────────
import legCoreL from './assets/leg-core-l.svg';
import legCoreR from './assets/leg-core-r.svg';
import feetOffsets from './assets/feet-offsets.svg';
import axaJacket from './assets/axa-jacket.svg';
import axaWing from './assets/axa-wing-l.svg'; // all 4 feathers share identical geometry
import neatLegL from './assets/neat-leg-l.svg';
import neatBase from './assets/neat-base.svg';
import neatStar from './assets/neat-star.svg';
import nlbpLegL from './assets/nlbp-leg-l.svg';
import nlbpLegR from './assets/nlbp-leg-r.svg';
import nlbpBase from './assets/nlbp-base.svg';

// ── Overlay icons ───────────────────────────────────────────────────────────
import overlayMyLocation from './assets/icon-my-location.svg';
import overlayEncrypted from './assets/icon-encrypted.svg';
import overlayPsychiatry from './assets/icon-psychiatry.svg';
import overlayBolt from './assets/icon-bolt.svg';
import overlayKey from './assets/icon-key.svg';
import overlayMail from './assets/icon-mail.svg';

// ── Icône overlay par catégorie (seul élément qui dépend du type d'achievement) ─
const CATEGORY_OVERLAY_ICONS = {
  contract:   overlayMyLocation,
  axa:        overlayEncrypted,
  offset:     overlayPsychiatry,
  lead:       overlayBolt,
  protected:  overlayKey,
  neat:       null,
  nlbp:       overlayMail,
  myselectra: null,
};

// ── Layouts par niveau de trophée ───────────────────────────────────────────
// La forme (extras) dépend du niveau, l'icône overlay dépend de la catégorie.
// overlayRect: position de l'icône 24×24 centrée sur le body hex.
const LEVEL_LAYOUTS = {
  empty: {
    viewBox: { w: 51.58, h: 59.93 },
    body: { x: 0, y: 0, w: 51.58, h: 59.93 },
    overlayRect: { x: 13.79, y: 17.97, w: 24, h: 24 },
    extras: [],
  },
  beginner: {
    viewBox: { w: 51.58, h: 59.93 },
    body: { x: 0, y: 0, w: 51.58, h: 59.93 },
    overlayRect: { x: 13.79, y: 17.97, w: 24, h: 24 },
    extras: [],
  },
  bronze: {
    viewBox: { w: 55, h: 65 },
    body: { x: 0, y: 0, w: 55, h: 63.91 },
    overlayRect: { x: 15.5, y: 19.96, w: 24, h: 24 },
    extras: [
      { src: legCoreL, x: 6.16, y: 40.26, w: 18.37, h: 23.53 },
      { src: legCoreR, x: 32.34, y: 40.26, w: 18.37, h: 23.53, flipX: true },
    ],
  },
  silver: {
    viewBox: { w: 54.54, h: 73.77 },
    body: { x: 0, y: 0, w: 54.54, h: 63.37 },
    overlayRect: { x: 15.27, y: 19.69, w: 24, h: 24 },
    extras: [
      { src: feetOffsets, x: 6.11, y: 36.87, w: 42.54, h: 36.88 },
    ],
  },
  gold: {
    viewBox: { w: 62.31, h: 71.61 },
    body: { x: 5.37, y: 0, w: 51.58, h: 59.93 },
    overlayRect: { x: 19.16, y: 17.97, w: 24, h: 24 },
    extras: [
      { src: neatLegL, x: 0, y: 21.04, w: 7.43, h: 17.54 },
      { src: neatLegL, x: 54.88, y: 21.04, w: 7.43, h: 17.54, flipX: true },
      { src: neatBase, x: 11.14, y: 36.93, w: 40.23, h: 34.68 },
      { src: neatStar, x: 21.97, y: 54.67, w: 18.57, h: 16.09 },
    ],
  },
  platinium: {
    viewBox: { w: 95.76, h: 73.24 },
    body: { x: 22.11, y: 0, w: 51.58, h: 59.93 },
    overlayRect: { x: 35.9, y: 17.97, w: 24, h: 24 },
    extras: [
      { src: nlbpLegL, x: 0, y: 7.36, w: 36.93, h: 29.61 },
      { src: nlbpLegR, x: 58.83, y: 7.63, w: 36.93, h: 29.61, flipX: true },
      { src: nlbpBase, x: 36.55, y: 38.58, w: 22.69, h: 34.66 },
    ],
  },
  diamond: {
    viewBox: { w: 85, h: 73 },
    body: { x: 16.5, y: 0, w: 51.58, h: 59.93 },
    overlayRect: { x: 30.29, y: 17.97, w: 24, h: 24 },
    extras: [
      { src: axaWing, x: 0, y: 17.33, w: 8.52, h: 20.12, rotate: -90, flipY: true },
      { src: axaWing, x: 7.1, y: 24.9, w: 8.52, h: 20.12, rotate: -90, flipY: true },
      { src: axaWing, x: 57.33, y: 17.33, w: 8.52, h: 20.12, rotate: -90 },
      { src: axaWing, x: 64.43, y: 24.9, w: 8.52, h: 20.12, rotate: -90 },
      { src: axaJacket, x: 35.26, y: 38.37, w: 14.24, h: 33.22 },
    ],
  },
  elite: {
    viewBox: { w: 85, h: 73 },
    body: { x: 16.5, y: 0, w: 51.58, h: 59.93 },
    overlayRect: { x: 30.29, y: 17.97, w: 24, h: 24 },
    extras: [
      { src: axaWing, x: 0, y: 17.33, w: 8.52, h: 20.12, rotate: -90, flipY: true },
      { src: axaWing, x: 7.1, y: 24.9, w: 8.52, h: 20.12, rotate: -90, flipY: true },
      { src: axaWing, x: 57.33, y: 17.33, w: 8.52, h: 20.12, rotate: -90 },
      { src: axaWing, x: 64.43, y: 24.9, w: 8.52, h: 20.12, rotate: -90 },
      { src: axaJacket, x: 35.26, y: 38.37, w: 14.24, h: 33.22 },
      { src: neatBase, x: 22.27, y: 36.93, w: 40.23, h: 34.68 },
      { src: neatStar, x: 33.1, y: 54.67, w: 18.57, h: 16.09 },
    ],
  },
};

/**
 * Computes inline style for a positioned layer (extra, body, or overlay).
 * Uses inline style={} because each layer has unique computed dimensions.
 */
const getLayerStyle = (layer, scale) => {
  const w = layer.w * scale;
  const h = layer.h * scale;
  let left = layer.x * scale;
  let top = layer.y * scale;

  if (layer.rotate && Math.abs(layer.rotate) === 90) {
    const delta = (h - w) / 2;
    left += delta;
    top -= delta;
  }

  const style = {
    position: 'absolute',
    left: `${left / 16}rem`,
    top: `${top / 16}rem`,
    width: `${w / 16}rem`,
    height: `${h / 16}rem`,
    pointerEvents: 'none',
  };
  const transforms = [];
  if (layer.flipX) transforms.push('scaleX(-1)');
  if (layer.flipY) transforms.push('scaleY(-1)');
  if (layer.rotate) transforms.push(`rotate(${layer.rotate}deg)`);
  if (transforms.length > 0) style.transform = transforms.join(' ');
  return style;
};

const TrophyBadge = ({ categoryId, trophyColor = 'empty', bodyWidth = 48 }) => {
  const layout = LEVEL_LAYOUTS[trophyColor] || LEVEL_LAYOUTS.empty;
  const overlayIcon = CATEGORY_OVERLAY_ICONS[categoryId] || null;

  const { viewBox, body, overlayRect, extras } = layout;
  const scale = bodyWidth / body.w;
  const renderedW = viewBox.w * scale;
  const renderedH = viewBox.h * scale;

  const bodyColors = LEVEL_BODY_COLORS[trophyColor] || LEVEL_BODY_COLORS.empty;

  return (
    <TrophyContainer $width={renderedW} $height={renderedH}>
      {/* Extras rendered first (behind body) */}
      {extras.map((layer, i) => (
        <LayerImg
          key={`extra-${i}`}
          src={layer.src}
          alt=""
          style={getLayerStyle(layer, scale)}
        />
      ))}

      {/* Body hex (inline SVG with dynamic colors) */}
      <div style={getLayerStyle(body, scale)}>
        <TrophyBodySvg colors={bodyColors} />
      </div>

      {/* Overlay icon (on top of body) — dépend de la catégorie */}
      {overlayIcon && overlayRect && (
        <LayerImg
          src={overlayIcon}
          alt=""
          style={getLayerStyle(overlayRect, scale)}
        />
      )}
    </TrophyContainer>
  );
};

export default TrophyBadge;
